/**
 * /videos — FapHouse VOD index page
 * /videos/:category — category-filtered video pages
 *
 * SEO targets:
 *   /videos            → "free cam girl videos", "cam girl clips", "adult cam videos"
 *   /videos/latina     → "latina cam videos", "latina porn clips"
 *   /videos/milf       → "milf cam videos", etc.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Chip, Skeleton, Button, Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { fetchFapHouseVideos } from "../contexts/FapHouseContext";

// ─── Category config ──────────────────────────────────────────────────────────
export const VIDEO_CATEGORIES = [
  { slug: "latina",       label: "Latina",        keyword: "latina",       emoji: "🌶️" },
  { slug: "ebony",        label: "Ebony",         keyword: "ebony",        emoji: "✨" },
  { slug: "asian",        label: "Asian",         keyword: "asian",        emoji: "🌸" },
  { slug: "milf",         label: "MILF",          keyword: "milf",         emoji: "💋" },
  { slug: "teen",         label: "Teen",          keyword: "teen",         emoji: "🎀" },
  { slug: "blonde",       label: "Blonde",        keyword: "blonde",       emoji: "👱‍♀️" },
  { slug: "brunette",     label: "Brunette",      keyword: "brunette",     emoji: "🤎" },
  { slug: "redhead",      label: "Redhead",       keyword: "redhead",      emoji: "🔥" },
  { slug: "mature",       label: "Mature",        keyword: "mature",       emoji: "💎" },
  { slug: "big-tits",     label: "Big Tits",      keyword: "big tits",     emoji: "🍒" },
  { slug: "big-ass",      label: "Big Ass",       keyword: "big ass",      emoji: "🍑" },
  { slug: "petite",       label: "Petite",        keyword: "petite",       emoji: "🌟" },
  { slug: "amateur",      label: "Amateur",       keyword: "amateur",      emoji: "🎬" },
  { slug: "feet",         label: "Feet",          keyword: "feet",         emoji: "🦶" },
  { slug: "bdsm",         label: "BDSM",          keyword: "bdsm",         emoji: "⛓️" },
  { slug: "squirt",       label: "Squirt",        keyword: "squirt",       emoji: "💦" },
  { slug: "lesbian",      label: "Lesbian",       keyword: "lesbian",      emoji: "🏳️‍🌈" },
  { slug: "natural-tits", label: "Natural Tits",  keyword: "natural tits", emoji: "🌿" },
];

const VIDEOS_PER_PAGE = 24;

// ─── SEO head ─────────────────────────────────────────────────────────────────
const SEOHead = ({ title, description, canonical, keywords }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        prop ? el.setAttribute("property", name) : el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (description) { setMeta("description", description); setMeta("og:description", description, true); }
    if (keywords) setMeta("keywords", keywords);
    if (title) { setMeta("og:title", title, true); }
    if (canonical) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
      link.href = canonical;
      setMeta("og:url", canonical, true);
    }
  }, [title, description, canonical, keywords]);
  return null;
};

// ─── Styled ───────────────────────────────────────────────────────────────────
const VideoCard = styled(Box)({
  position: "relative",
  borderRadius: 8,
  overflow: "hidden",
  cursor: "pointer",
  display: "block",
  textDecoration: "none",
  "&:hover img": { transform: "scale(1.06)" },
  "&:hover .play-overlay": { opacity: 1 },
});

const Thumb = styled("img")({
  width: "100%",
  aspectRatio: "16/9",
  objectFit: "cover",
  display: "block",
  transition: "transform 0.25s",
  background: "#111",
});

const PlayOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.2s",
});

const fmtDuration = (secs) => {
  if (!secs) return null;
  const s = parseInt(secs, 10);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

// ─── Video grid ───────────────────────────────────────────────────────────────
const VideoGrid = ({ videos, loading }) => (
  <Grid container spacing={1.5}>
    {loading
      ? Array.from({ length: VIDEOS_PER_PAGE }).map((_, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Skeleton variant="rectangular" sx={{ aspectRatio: "16/9", borderRadius: 1, bgcolor: "#1a1a1a" }} />
            <Skeleton width="70%" sx={{ bgcolor: "#1a1a1a", mt: 0.5 }} />
          </Grid>
        ))
      : videos.map((v) => (
          <Grid item xs={6} sm={4} md={3} key={v.id}>
            <VideoCard component={Link} to={`/vod/${v.slug || v.id}`}>
              <Thumb src={v.thumbnailUrl} alt={v.title} loading="lazy" />
              <PlayOverlay className="play-overlay">
                <Box sx={{
                  width: 44, height: 44, borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PlayArrowIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
              </PlayOverlay>
              {v.quality && (
                <Box sx={{
                  position: "absolute", top: 5, right: 5,
                  bgcolor: v.quality === "4k" ? "#9C27B0" : "#1565C0",
                  color: "#fff", fontSize: "0.55rem", fontWeight: 800,
                  px: "5px", py: "1px", borderRadius: 0.5, textTransform: "uppercase",
                }}>
                  {v.quality}
                </Box>
              )}
              {v.duration && (
                <Box sx={{
                  position: "absolute", bottom: 28, right: 5,
                  bgcolor: "rgba(0,0,0,0.75)", color: "#fff",
                  fontSize: "0.6rem", px: "4px", borderRadius: 0.5,
                }}>
                  {fmtDuration(v.duration)}
                </Box>
              )}
              <Box sx={{ p: "4px 4px 6px", bgcolor: "#0d0d0d" }}>
                <Typography sx={{
                  fontSize: "0.72rem", color: "#ccc", lineHeight: 1.3,
                  display: "-webkit-box", WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                  {v.title}
                </Typography>
              </Box>
            </VideoCard>
          </Grid>
        ))
    }
  </Grid>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function VideosPage() {
  const { category } = useParams(); // undefined on /videos
  const navigate = useNavigate();

  const catConfig = category
    ? VIDEO_CATEGORIES.find((c) => c.slug === category)
    : null;

  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setVideos([]);
    try {
      const keyword = catConfig?.keyword || "";
      // Fetch enough for pagination — FapHouse widget caps at 100
      const results = await fetchFapHouseVideos({ keyword, quantity: 100 });
      setTotal(results.length);
      setVideos(results);
    } catch (e) {
      console.error("VideosPage load error:", e);
    } finally {
      setLoading(false);
    }
  }, [catConfig]);

  useEffect(() => {
    setPage(1);
    loadVideos();
    window.scrollTo(0, 0);
  }, [category, loadVideos]);

  const pageVideos = videos.slice((page - 1) * VIDEOS_PER_PAGE, page * VIDEOS_PER_PAGE);
  const pageCount  = Math.ceil(total / VIDEOS_PER_PAGE);

  // SEO
  const catLabel = catConfig?.label || "All";
  const pageTitle = catConfig
    ? `${catConfig.label} Cam Videos — Free Clips | InstaCamGirls`
    : "Free Cam Girl Videos — Watch Clips | InstaCamGirls";
  const pageDesc = catConfig
    ? `Watch free ${catConfig.label.toLowerCase()} cam girl videos on InstaCamGirls. Browse the best ${catConfig.label.toLowerCase()} clips, recorded shows, and live cam highlights.`
    : "Watch free cam girl videos on InstaCamGirls. Browse thousands of clips across all categories — Latina, MILF, Ebony, Asian, and more. Live cams available 24/7.";
  const pageKeywords = catConfig
    ? `${catConfig.label.toLowerCase()} cam videos, ${catConfig.label.toLowerCase()} cam girl clips, free ${catConfig.label.toLowerCase()} porn, ${catConfig.label.toLowerCase()} cam show`
    : "cam girl videos, free cam clips, cam girl porn, live cam videos, adult cam videos";
  const canonical = catConfig
    ? `https://instacamgirls.com/videos/${catConfig.slug}`
    : "https://instacamgirls.com/videos";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0d0d0d", color: "#fff", px: { xs: 1.5, md: 3 }, py: 2.5 }}>
      <SEOHead title={pageTitle} description={pageDesc} canonical={canonical} keywords={pageKeywords} />

      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" component="h1" sx={{
          fontWeight: 800, fontSize: { xs: "1.4rem", md: "2rem" },
          background: "linear-gradient(90deg, #e91e8c, #ff6b35)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          {catConfig ? `${catConfig.emoji} ${catConfig.label} Cam Videos` : "🎬 Free Cam Girl Videos"}
        </Typography>
        <Typography sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5 }}>
          {catConfig
            ? `Browse free ${catConfig.label.toLowerCase()} cam clips and recorded shows`
            : "Browse thousands of free cam girl clips across all categories"}
        </Typography>
      </Box>

      {/* Category filter chips */}
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2.5 }}>
        <Chip
          label="All Videos"
          onClick={() => navigate("/videos")}
          sx={{
            bgcolor: !category ? "#e91e8c" : "#1a1a1a",
            color: !category ? "#fff" : "#aaa",
            fontWeight: !category ? 700 : 400,
            fontSize: "0.75rem",
            "&:hover": { bgcolor: !category ? "#c2185b" : "#333" },
            cursor: "pointer",
          }}
        />
        {VIDEO_CATEGORIES.map((cat) => (
          <Chip
            key={cat.slug}
            label={`${cat.emoji} ${cat.label}`}
            onClick={() => navigate(`/videos/${cat.slug}`)}
            sx={{
              bgcolor: category === cat.slug ? "#e91e8c" : "#1a1a1a",
              color: category === cat.slug ? "#fff" : "#aaa",
              fontWeight: category === cat.slug ? 700 : 400,
              fontSize: "0.75rem",
              "&:hover": { bgcolor: category === cat.slug ? "#c2185b" : "#333" },
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      {/* Video grid */}
      <VideoGrid videos={pageVideos} loading={loading} />

      {/* Pagination */}
      {!loading && pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, val) => { setPage(val); window.scrollTo(0, 0); }}
            sx={{
              "& .MuiPaginationItem-root": { color: "#aaa" },
              "& .Mui-selected": { bgcolor: "#e91e8c !important", color: "#fff" },
            }}
          />
        </Box>
      )}

      {/* SEO text + internal links */}
      <Box sx={{ mt: 5, pt: 3, borderTop: "1px solid #1a1a1a" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#ccc", mb: 1.5, fontSize: "0.95rem" }}>
          Browse by Category
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
          {VIDEO_CATEGORIES.map((cat) => (
            <Button
              key={cat.slug}
              component={Link}
              to={`/videos/${cat.slug}`}
              size="small"
              sx={{
                color: "#e91e8c", borderColor: "#333", fontSize: "0.75rem",
                textTransform: "none", border: "1px solid #333", borderRadius: 1,
                "&:hover": { borderColor: "#e91e8c", bgcolor: "rgba(233,30,140,0.05)" },
              }}
            >
              {cat.label} Videos
            </Button>
          ))}
        </Box>

        <Typography sx={{ color: "#555", fontSize: "0.78rem", lineHeight: 1.7, maxWidth: 700 }}>
          InstaCamGirls features free cam girl videos from the world's top live cam platforms.
          Watch {catLabel.toLowerCase()} clips, recorded shows, and highlights from live performances.
          Browse <Link to="/category/latina-cam-girls" style={{ color: "#e91e8c" }}>Latina cam girls</Link>,{" "}
          <Link to="/category/milf-cam-girls" style={{ color: "#e91e8c" }}>MILF cams</Link>,{" "}
          <Link to="/category/ebony-cam-girls" style={{ color: "#e91e8c" }}>Ebony performers</Link>, and more.
          All content is provided by verified adult platforms.{" "}
          <Link to="/" style={{ color: "#e91e8c" }}>Watch live cams now →</Link>
        </Typography>
      </Box>
    </Box>
  );
}
